import Button from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import { Popup } from "devextreme-react/popup";
import { confirm } from "devextreme/ui/dialog";
import SelectBox from "devextreme-react/select-box";
import TagBox from "devextreme-react/tag-box";
import { Tooltip } from "devextreme-react/tooltip";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { PhaseWorkflowType } from "../../../dtos/PhaseWorkflowType";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import { UserRole } from "../../../dtos/UserRole";
import { PermissionType } from "../../../dtos/PermissionType";
import { PhaseStatus } from "../../../dtos/PhaseStatus";
import { StepStatus } from "../../../dtos/StepStatus";
import { groupRoleItems } from "../../shared/tasklist/config/audienceConfig";
import TemplateGroupBox, { GROUP_REMOVED_COLOR } from "./components/TemplateGroupBox";

// Mode determines what permissions we're editing:
// - "task": Task's own permissions (task.settings.workflow.permissions)
// - "subtask": Subtask's own permissions (subtask.settings.workflow.permissions)
// - "defaultSubtask": Task's default subtask permissions (task.settings.defaultStepSettings.workflow.permissions)
export type PermissionsMode = "task" | "subtask" | "defaultSubtask";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    configOptions: WorkflowConfigOptionsDto;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
    mode?: PermissionsMode; // Defaults based on whether task or subtask is provided
    workflowType?: PhaseWorkflowType; // Used to determine if we should show simplified UI for STEP workflows
};

type TaskStatus = PhaseStatus | StepStatus;

type PermissionEntry = {
    role: UserRole;
    permission: PermissionType;
    statuses: TaskStatus[];
};

// A role or status row stays on screen after it is added even before any permission is
// picked for it, so the user has somewhere to pick one.
type StatusRowKey = { role: UserRole; status: TaskStatus };

const removedTextStyle: React.CSSProperties = { textDecoration: "line-through", color: GROUP_REMOVED_COLOR };

export default function TemplatePermissionsTab({ task, subtask, configOptions, onSave, mode, workflowType }: Props) {
    // Determine effective mode if not explicitly provided
    const effectiveMode: PermissionsMode = mode ?? (task ? "task" : "subtask");
    const isTaskMode = effectiveMode === "task";
    const isSubtaskMode = effectiveMode === "subtask";
    const isDefaultSubtaskMode = effectiveMode === "defaultSubtask";

    // For STEP workflows at task level, permissions apply regardless of status
    const useSimplifiedPermissions = isTaskMode && workflowType === PhaseWorkflowType.Step;

    // Get the current workflow config based on mode
    const currentConfig = isTaskMode
        ? task?.settings?.workflow?.permissions
        : isDefaultSubtaskMode
            ? task?.settings?.defaultStepSettings?.workflow?.permissions
            : subtask?.settings?.workflow?.permissions;

    // Get available options - defaultSubtask uses subtask options (since it configures subtask behavior)
    const roleOptions = configOptions.roles.filter(r => !r.notificationOnly);
    const useSubtaskOptions = isSubtaskMode || isDefaultSubtaskMode;
    const permissionOptions = useSubtaskOptions ? configOptions.subtask.permissions : configOptions.task.permissions;
    const statusOptions = useSubtaskOptions ? configOptions.subtask.statuses : configOptions.task.statuses;

    // Build initial state from current config
    const buildInitialState = (): PermissionEntry[] => {
        const entries: PermissionEntry[] = [];

        if (currentConfig?.roleConfigs) {
            for (const roleConfig of currentConfig.roleConfigs) {
                for (const permEntry of roleConfig.permissions ?? []) {
                    entries.push({
                        role: roleConfig.role,
                        permission: permEntry.permission,
                        statuses: permEntry.statuses ?? []
                    });
                }
            }
        }

        return entries;
    };

    const [permissions, setPermissions] = useState<PermissionEntry[]>(buildInitialState);

    // Track saved state to detect dirty changes (use state so changes trigger re-render)
    const [savedPermissions, setSavedPermissions] = useState<PermissionEntry[]>(buildInitialState);

    // Roles and status rows the user added that have no permissions picked yet
    const [emptyRoles, setEmptyRoles] = useState<UserRole[]>([]);
    const [emptyStatusRows, setEmptyStatusRows] = useState<StatusRowKey[]>([]);

    const [addRoleVisible, setAddRoleVisible] = useState(false);
    const [addStatusForRole, setAddStatusForRole] = useState<UserRole | null>(null);
    // Roles start collapsed so the tab opens as a list of who is configured; the count on each
    // header says how much is behind it.
    const [expandedRoles, setExpandedRoles] = useState<UserRole[]>([]);

    const toggleExpanded = (role: UserRole) => {
        setExpandedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    };

    // Removals are staged rather than applied: the row stays on screen struck through until the
    // save goes through, so an accidental click on the trash costs one click to undo.
    const [removedRoles, setRemovedRoles] = useState<UserRole[]>([]);
    const [removedStatusRows, setRemovedStatusRows] = useState<StatusRowKey[]>([]);

    const isRoleRemoved = (role: UserRole) => removedRoles.includes(role);
    const isStatusRemoved = (role: UserRole, status: TaskStatus) =>
        isRoleRemoved(role) || removedStatusRows.some(r => r.role === role && r.status === status);

    const clearPendingRows = () => {
        setEmptyRoles([]);
        setEmptyStatusRows([]);
        setRemovedRoles([]);
        setRemovedStatusRows([]);
    };

    // Create a stable key for the current config to detect changes after save
    const configKey = useMemo(() => JSON.stringify(currentConfig), [currentConfig]);

    // Reset state when task/subtask changes or when config changes (e.g., after save with normalization)
    useEffect(() => {
        const initial = buildInitialState();
        setPermissions(initial);
        setSavedPermissions(initial);
        clearPendingRows();
    }, [task?.id, subtask?.id, configKey]);

    // What the save would write: everything except the rows staged for removal
    const keptPermissions = useMemo(() => permissions
        .filter(p => !isRoleRemoved(p.role))
        .map(p => ({ ...p, statuses: p.statuses.filter(s => !isStatusRemoved(p.role, s)) }))
        .filter(p => p.statuses.length > 0),
        [permissions, removedRoles, removedStatusRows]);

    // Check if permissions are dirty (different from saved state)
    const isDirty = useMemo(() => {
        // Build a comparable string representation for each
        const toComparable = (entries: PermissionEntry[]) => {
            return entries
                .filter(e => e.statuses.length > 0)
                .map(e => `${e.role}|${e.permission}|${[...e.statuses].sort().join(",")}`)
                .sort()
                .join(";");
        };

        return toComparable(keptPermissions) !== toComparable(savedPermissions);
    }, [keptPermissions, savedPermissions]);

    // Update: given a role and status, set which permissions are allowed
    const updateStatusPermissions = useCallback((role: UserRole, status: TaskStatus, newPermissions: PermissionType[]) => {
        setPermissions(prev => {
            // Build a map of permission -> has this status for this role
            const currentPermsForStatus = new Set(
                prev.filter(p => p.role === role && p.statuses.includes(status)).map(p => p.permission)
            );
            const newPermsSet = new Set(newPermissions);

            // Check if there's actually a change
            if (currentPermsForStatus.size === newPermsSet.size &&
                [...currentPermsForStatus].every(p => newPermsSet.has(p))) {
                return prev; // No change, return same reference to prevent re-render
            }

            // Remove this status from all permission entries for this role
            let updated = prev.map(p => {
                if (p.role === role) {
                    return { ...p, statuses: p.statuses.filter(s => s !== status) };
                }
                return p;
            });

            // Add this status to each selected permission for this role
            for (const perm of newPermissions) {
                const existingIdx = updated.findIndex(p => p.role === role && p.permission === perm);
                if (existingIdx >= 0) {
                    updated[existingIdx] = {
                        ...updated[existingIdx],
                        statuses: [...updated[existingIdx].statuses, status]
                    };
                } else {
                    updated.push({ role, permission: perm, statuses: [status] });
                }
            }

            return updated;
        });
    }, []);

    // Toggle permission on/off (used in simplified STEP workflow UI)
    // For STEP workflows, task permissions are status-independent - we just need one status to indicate "enabled"
    const togglePermission = useCallback((role: UserRole, permission: PermissionType, enabled: boolean) => {
        setPermissions(prev => {
            // Remove existing entry for this role+permission
            const updated = prev.filter(p => !(p.role === role && p.permission === permission));

            if (enabled) {
                // Add entry with a single status - permission check ignores status for STEP workflows
                const singleStatus = statusOptions[0]?.value;
                if (singleStatus !== undefined) {
                    updated.push({ role, permission, statuses: [singleStatus] });
                }
            }

            return updated;
        });
    }, [statusOptions]);

    // Check if a permission is enabled for a role (has any statuses configured)
    const isPermissionEnabled = useCallback((role: UserRole, permission: PermissionType): boolean => {
        return permissions.some(p => p.role === role && p.permission === permission && p.statuses.length > 0);
    }, [permissions]);

    // Memoize permissions lookup by role+status to avoid creating new arrays on each render
    const permissionsByRoleAndStatus = useMemo(() => {
        const map = new Map<string, PermissionType[]>();
        for (const entry of permissions) {
            for (const status of entry.statuses) {
                const key = `${entry.role}-${status}`;
                const existing = map.get(key) || [];
                existing.push(entry.permission);
                map.set(key, existing);
            }
        }
        return map;
    }, [permissions]);

    // Sort statuses by order
    const sortedStatuses = useMemo(() =>
        [...statusOptions].sort((a, b) => a.order - b.order),
        [statusOptions]
    );

    const roleLabel = useCallback((role: UserRole) =>
        roleOptions.find(r => r.value === role)?.label ?? `Role ${role}`,
        [roleOptions]
    );

    const statusLabel = useCallback((status: TaskStatus) =>
        sortedStatuses.find(s => s.value === status)?.label ?? `Status ${status}`,
        [sortedStatuses]
    );

    // Roles shown as groups: those with permissions plus any just added.
    // NIH roles first, then grantee, alphabetical within each - the same grouping the
    // add dialog uses.
    const configuredRoles = useMemo(() => {
        const inUse = new Set(permissions.filter(p => p.statuses.length > 0).map(p => p.role));
        return roleOptions
            .filter(r => inUse.has(r.value) || emptyRoles.includes(r.value))
            .sort((a, b) => (Number(a.isExternal) - Number(b.isExternal)) || a.label.localeCompare(b.label));
    }, [permissions, emptyRoles, roleOptions]);

    // Statuses shown under a role: those with permissions plus any just added
    const statusesForRole = useCallback((role: UserRole) => {
        const inUse = new Set<TaskStatus>();
        for (const entry of permissions) {
            if (entry.role !== role) continue;
            for (const status of entry.statuses) inUse.add(status);
        }
        for (const row of emptyStatusRows) {
            if (row.role === role) inUse.add(row.status);
        }
        return sortedStatuses.filter(s => inUse.has(s.value));
    }, [permissions, emptyStatusRows, sortedStatuses]);

    // Add the chosen permissions to the chosen statuses for one role
    const grantPermissions = (role: UserRole, statuses: TaskStatus[], perms: PermissionType[]) => {
        setPermissions(prev => {
            const updated = prev.map(p => ({ ...p, statuses: [...p.statuses] }));
            for (const perm of perms) {
                let entry = updated.find(p => p.role === role && p.permission === perm);
                if (!entry) {
                    entry = { role, permission: perm, statuses: [] };
                    updated.push(entry);
                }
                for (const status of statuses) {
                    if (!entry.statuses.includes(status)) entry.statuses.push(status);
                }
            }
            return updated;
        });

        // What was just added has to be visible, so a collapsed or struck-through role comes back
        restoreRole(role);
        setExpandedRoles(prev => prev.includes(role) ? prev : [...prev, role]);
        setEmptyRoles(prev => prev.includes(role) ? prev : [...prev, role]);
        setEmptyStatusRows(prev => [
            ...prev.filter(r => !(r.role === role && statuses.includes(r.status))),
            ...statuses.map(status => ({ role, status }))
        ]);
    };

    const removeRole = (role: UserRole) => {
        setRemovedRoles(prev => prev.includes(role) ? prev : [...prev, role]);
        // A struck-through role has nothing worth reading inside it
        setExpandedRoles(prev => prev.filter(r => r !== role));
    };

    const restoreRole = (role: UserRole) => {
        setRemovedRoles(prev => prev.filter(r => r !== role));
        setRemovedStatusRows(prev => prev.filter(r => r.role !== role));
    };

    const removeStatus = (role: UserRole, status: TaskStatus) => {
        setRemovedStatusRows(prev => prev.some(r => r.role === role && r.status === status)
            ? prev
            : [...prev, { role, status }]);
    };

    const restoreStatus = (role: UserRole, status: TaskStatus) => {
        setRemovedStatusRows(prev => prev.filter(r => !(r.role === role && r.status === status)));
    };

    // Restore defaults for every role on this tab
    const handleRestoreDefaults = () => {
        const defaultEntries: PermissionEntry[] = [];

        // Handle task vs subtask configs separately to avoid union type issues
        if (useSubtaskOptions) {
            // per-subtask overrides are only used for DC Design Completion (each subtask has a different owner)
            const overrides = configOptions.subtask.defaultPermissionOverrides;
            const subtaskId = subtask?.id;
            const overrideConfig = subtaskId && overrides ? overrides[subtaskId] : undefined;
            const roleConfigs = overrideConfig?.roleConfigs ?? configOptions.subtask.defaultPermissions?.roleConfigs;
            for (const roleConfig of roleConfigs ?? []) {
                for (const permEntry of roleConfig.permissions ?? []) {
                    defaultEntries.push({
                        role: roleConfig.role,
                        permission: permEntry.permission,
                        statuses: permEntry.statuses ?? []
                    });
                }
            }
        } else {
            const roleConfigs = configOptions.task.defaultPermissions?.roleConfigs;
            for (const roleConfig of roleConfigs ?? []) {
                for (const permEntry of roleConfig.permissions ?? []) {
                    defaultEntries.push({
                        role: roleConfig.role,
                        permission: permEntry.permission,
                        statuses: permEntry.statuses ?? []
                    });
                }
            }
        }

        setPermissions(defaultEntries);
        clearPendingRows();
    };

    // Clear permissions for every role on this tab
    const handleClearAll = async () => {
        // One click empties every role, so it is worth asking first even though a discard undoes it
        const confirmed = await confirm(
            `Remove the permissions for all ${configuredRoles.length} roles on this tab?`,
            "Clear Permissions"
        );
        if (!confirmed) return;

        setPermissions([]);
        clearPendingRows();
    };

    const handleDiscard = () => {
        setPermissions([...savedPermissions]);
        clearPendingRows();
    };

    const handleSave = async () => {
        // Convert permissions back to the config format
        const roleConfigs: any[] = [];

        for (const role of roleOptions) {
            const rolePerms = keptPermissions.filter(p => p.role === role.value);
            if (rolePerms.length > 0) {
                roleConfigs.push({
                    role: role.value,
                    permissions: rolePerms.map(p => ({
                        permission: p.permission,
                        statuses: p.statuses
                    }))
                });
            }
        }

        if (isTaskMode && task) {
            // Task's own permissions
            const updatedTask: DefaultPhaseDto = {
                ...task,
                settings: {
                    ...task.settings,
                    workflow: {
                        permissions: { roleConfigs },
                        actions: task.settings?.workflow?.actions ?? null
                    }
                }
            };
            await onSave(updatedTask);
        } else if (isDefaultSubtaskMode && task) {
            // Task's default subtask permissions
            const currentDefaults = task.settings?.defaultStepSettings;
            const updatedTask: DefaultPhaseDto = {
                ...task,
                settings: {
                    ...task.settings,
                    defaultStepSettings: {
                        unrestrictedUpload: currentDefaults?.unrestrictedUpload ?? false,
                        fileRestrictions: currentDefaults?.fileRestrictions ?? null,
                        fileLimit: currentDefaults?.fileLimit ?? null,
                        allowedExtensions: currentDefaults?.allowedExtensions ?? null,
                        offsetStart: currentDefaults?.offsetStart ?? null,
                        offsetEnd: currentDefaults?.offsetEnd ?? null,
                        dueDate: currentDefaults?.dueDate ?? null,
                        requestFileAttachments: currentDefaults?.requestFileAttachments ?? null,
                        triggerActions: currentDefaults?.triggerActions ?? null,
                        initialStepStatus: currentDefaults?.initialStepStatus ?? null,
                        notifications: currentDefaults?.notifications ?? null,
                        workflow: {
                            permissions: { roleConfigs },
                            transitions: currentDefaults?.workflow?.transitions ?? null
                        },
                        workflowType: currentDefaults?.workflowType ?? null,
                        isCore: currentDefaults?.isCore ?? false,
                    }
                }
            };
            await onSave(updatedTask);
        } else if (isSubtaskMode && subtask) {
            // Subtask's own permissions
            const updatedSubtask: DefaultStepDto = {
                ...subtask,
                settings: {
                    ...subtask.settings,
                    workflow: {
                        permissions: { roleConfigs },
                        transitions: subtask.settings?.workflow?.transitions ?? null
                    }
                }
            };
            await onSave(updatedSubtask);
        }

        // Note: savedPermissions is updated by the useEffect when the parent updates the props with fresh data
    };

    const permissionItems = useMemo(() => permissionOptions.map(p => ({
        id: p.value,
        name: p.label,
        description: p.description
    })), [permissionOptions]);

    const renderPermissionItem = (item: { name: string; description: string | null }) => (
        <div>
            <span style={{ fontWeight: 500 }}>{item.name}</span>
            {item.description && (
                <span style={{ color: "#888", marginLeft: "8px", fontSize: "12px" }}>
                    - {item.description}
                </span>
            )}
        </div>
    );

    // One status row: the permissions a role has while the item sits at that status
    const renderStatusRow = (role: UserRole, status: TaskStatus) => {
        const removed = isStatusRemoved(role, status);
        return (
            <div
                key={`${role}-${status}`}
                data-testid="permission-status-row"
                data-removed={removed}
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
                <div style={{
                    width: "180px",
                    minWidth: "180px",
                    fontSize: "13px",
                    ...(removed ? removedTextStyle : { color: "#555" })
                }}>
                    {statusLabel(status)}
                </div>
                <TagBox
                    items={permissionItems}
                    displayExpr="name"
                    valueExpr="id"
                    value={permissionsByRoleAndStatus.get(`${role}-${status}`) || []}
                    onValueChanged={(e) => updateStatusPermissions(role, status, e.value ?? [])}
                    width="100%"
                    placeholder="Select permissions..."
                    showSelectionControls={true}
                    itemRender={renderPermissionItem}
                    readOnly={removed}
                />
                {removed ? (
                    <Button
                        icon="undo"
                        stylingMode="text"
                        hint={`Keep ${statusLabel(status)}`}
                        onClick={() => restoreStatus(role, status)}
                    />
                ) : (
                    <Button
                        icon="trash"
                        stylingMode="text"
                        hint={`Remove ${statusLabel(status)}`}
                        onClick={() => removeStatus(role, status)}
                    />
                )}
            </div>
        );
    };

    // Simplified STEP workflow task-level permissions - no statuses, just checkboxes
    const renderSimplifiedPermissions = (role: UserRole) => (
        <div style={{ display: "grid", gap: "8px" }}>
            <p style={{ color: "#666", fontSize: "13px", margin: 0 }}>
                These permissions apply to the task regardless of its current status.
            </p>
            {permissionOptions.map(perm => (
                <div key={perm.value} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <CheckBox
                        value={isPermissionEnabled(role, perm.value)}
                        onValueChanged={(e) => togglePermission(role, perm.value, e.value ?? false)}
                    />
                    <div>
                        <span style={{ fontWeight: 500 }}>{perm.label}</span>
                        {perm.description && (
                            <span style={{ color: "#888", marginLeft: "8px", fontSize: "12px" }}>
                                - {perm.description}
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderRoleGroup = (role: UserRole) => {
        const statuses = statusesForRole(role);
        const allStatusesUsed = statuses.length === sortedStatuses.length;
        const removed = isRoleRemoved(role);
        const collapsed = !expandedRoles.includes(role);
        const kept = statuses.filter(s => !isStatusRemoved(role, s.value)).length;
        const statusCount = removed
            ? "removed when saved"
            : `${kept} ${kept === 1 ? "status" : "statuses"} configured`;

        const headerRight = (
            <>
                {!removed && !useSimplifiedPermissions && (
                    // A disabled DevExtreme button takes no pointer events, so the reason it is
                    // disabled hangs off the wrapper instead of the button itself.
                    <span id={`add-status-${role}`}>
                        <Button
                            icon="add"
                            text="Add Status"
                            stylingMode="text"
                            disabled={allStatusesUsed}
                            onClick={() => setAddStatusForRole(role)}
                        />
                        {allStatusesUsed && (
                            <Tooltip
                                target={`#add-status-${role}`}
                                showEvent="mouseenter"
                                hideEvent="mouseleave"
                                hideOnOutsideClick={false}
                                position="top"
                            >
                                <div style={{ padding: "8px 10px", fontSize: "12px" }}>
                                    {roleLabel(role)} already has a row for every status.
                                </div>
                            </Tooltip>
                        )}
                    </span>
                )}
                {removed ? (
                    <Button
                        icon="undo"
                        text="Undo"
                        stylingMode="text"
                        hint={`Keep ${roleLabel(role)}`}
                        onClick={() => restoreRole(role)}
                    />
                ) : (
                    <Button
                        icon="trash"
                        stylingMode="text"
                        hint={`Remove ${roleLabel(role)}`}
                        onClick={() => removeRole(role)}
                    />
                )}
            </>
        );

        return (
            <TemplateGroupBox
                key={role}
                title={roleLabel(role)}
                summary={useSimplifiedPermissions ? undefined : statusCount}
                summaryColor={removed ? GROUP_REMOVED_COLOR : undefined}
                headerRight={headerRight}
                collapsed={collapsed}
                onToggle={() => toggleExpanded(role)}
                removed={removed}
                testId="permission-role-group"
                toggleTestId="role-collapse-toggle"
            >
                {useSimplifiedPermissions ? renderSimplifiedPermissions(role) : (
                    <div style={{ display: "grid", gap: "8px" }}>
                        {statuses.length === 0 && (
                            <p style={{ color: "#888", fontSize: "13px", margin: 0 }}>
                                No statuses configured for this role.
                            </p>
                        )}
                        {statuses.map(status => renderStatusRow(role, status.value))}
                    </div>
                )}
            </TemplateGroupBox>
        );
    };

    const descriptionText = useSimplifiedPermissions
        ? "Configure task-level permissions for each role. Subtask permissions control what users can do at each status."
        : isDefaultSubtaskMode
            ? "Configure subtask permissions for each role. New subtasks will inherit these settings. View permission is automatically granted for any status where a role has other permissions."
            : "Each role below shows the permissions it has at each status. View permission is automatically granted for any status where a role has other permissions.";

    const unconfiguredRoles = roleOptions.filter(r => !configuredRoles.some(c => c.value === r.value));
    const allExpanded = configuredRoles.length > 0 && configuredRoles.every(r => expandedRoles.includes(r.value));
    const addStatusOptions = addStatusForRole === null
        ? []
        : sortedStatuses.filter(s => !statusesForRole(addStatusForRole).some(c => c.value === s.value));

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "16px" }}>
                <p style={{ flex: 1, margin: 0, color: "#666" }}>
                    {descriptionText}
                </p>
                {configuredRoles.length > 0 && (
                    <Button
                        icon={allExpanded ? "chevronup" : "chevrondown"}
                        text={allExpanded ? "Collapse All" : "Expand All"}
                        stylingMode="outlined"
                        onClick={() => setExpandedRoles(allExpanded ? [] : configuredRoles.map(r => r.value))}
                    />
                )}
                <span id="add-role">
                    <Button
                        icon="add"
                        text="Add Role"
                        stylingMode="outlined"
                        disabled={unconfiguredRoles.length === 0}
                        onClick={() => setAddRoleVisible(true)}
                    />
                    {unconfiguredRoles.length === 0 && (
                        <Tooltip
                            target="#add-role"
                            showEvent="mouseenter"
                            hideEvent="mouseleave"
                            hideOnOutsideClick={false}
                            position="top"
                        >
                            <div style={{ padding: "8px 10px", fontSize: "12px" }}>
                                Every role is already configured.
                            </div>
                        </Tooltip>
                    )}
                </span>
            </div>

            <div style={{ display: "grid", gap: "16px" }}>
                {configuredRoles.length === 0 && (
                    <p style={{ color: "#888" }}>No roles configured.</p>
                )}
                {configuredRoles.map(r => renderRoleGroup(r.value))}
            </div>

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save Permissions *" : "Save Permissions"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                    disabled={!isDirty}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={handleDiscard}
                    />
                )}
                <Button
                    text="Restore Defaults"
                    type="normal"
                    stylingMode="outlined"
                    onClick={handleRestoreDefaults}
                />
                <Button
                    text="Clear Permissions"
                    type="danger"
                    stylingMode="outlined"
                    onClick={handleClearAll}
                />
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "13px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>

            <AddPermissionsDialog
                visible={addRoleVisible}
                title="Add Role"
                roleItems={groupRoleItems(
                    unconfiguredRoles.map(r => ({ id: r.value, name: r.label })),
                    roleOptions
                )}
                statusItems={useSimplifiedPermissions ? [] : sortedStatuses.map(s => ({ id: s.value as TaskStatus, name: s.label }))}
                permissionItems={permissionItems}
                renderPermissionItem={renderPermissionItem}
                onCancel={() => setAddRoleVisible(false)}
                onAdd={(role, statuses, perms) => {
                    if (role === null) return;
                    // In simplified mode status carries no meaning - the checkboxes write their own marker status
                    const marker = statusOptions[0]?.value;
                    const applied = useSimplifiedPermissions
                        ? (marker === undefined ? [] : [marker])
                        : statuses;
                    grantPermissions(role, applied, perms);
                    setAddRoleVisible(false);
                }}
            />

            <AddPermissionsDialog
                visible={addStatusForRole !== null}
                title={addStatusForRole === null ? "Add Status" : `Add Status - ${roleLabel(addStatusForRole)}`}
                statusItems={addStatusOptions.map(s => ({ id: s.value as TaskStatus, name: s.label }))}
                permissionItems={permissionItems}
                renderPermissionItem={renderPermissionItem}
                onCancel={() => setAddStatusForRole(null)}
                onAdd={(_role, statuses, perms) => {
                    if (addStatusForRole !== null) grantPermissions(addStatusForRole, statuses, perms);
                    setAddStatusForRole(null);
                }}
            />
        </div>
    );
}

type RoleGroupItems = { key: string; items: { id: UserRole; name: string }[] }[];

type AddDialogProps = {
    visible: boolean;
    title: string;
    // Only the Add Role dialog picks a role; the Add Status dialog already knows it
    roleItems?: RoleGroupItems;
    statusItems: { id: TaskStatus; name: string }[];
    permissionItems: { id: PermissionType; name: string; description: string | null }[];
    renderPermissionItem: (item: { name: string; description: string | null }) => React.ReactElement;
    onCancel: () => void;
    onAdd: (role: UserRole | null, statuses: TaskStatus[], permissions: PermissionType[]) => void;
};

// Picks a role and/or statuses along with the permissions to grant, so a role can be set
// up in one step instead of one status at a time.
function AddPermissionsDialog({ visible, title, roleItems, statusItems, permissionItems, renderPermissionItem, onCancel, onAdd }: AddDialogProps) {
    const [role, setRole] = useState<UserRole | null>(null);
    const [statuses, setStatuses] = useState<TaskStatus[]>([]);
    const [perms, setPerms] = useState<PermissionType[]>([]);

    useEffect(() => {
        if (visible) {
            setRole(null);
            setStatuses([]);
            setPerms([]);
        }
    }, [visible]);

    const needsRole = roleItems != null;
    const needsStatus = statusItems.length > 0;
    // Adding with no permission picked would leave a row that the save silently drops
    const canAdd = (!needsRole || role !== null) && (!needsStatus || statuses.length > 0) && perms.length > 0;

    return (
        <Popup
            visible={visible}
            onHiding={onCancel}
            showCloseButton
            dragEnabled
            title={title}
            width={600}
            height="auto"
        >
            <div style={{ display: "grid", gap: "16px", padding: "8px" }}>
                {needsRole && (
                    <div>
                        <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Role</label>
                        <SelectBox
                            items={roleItems}
                            displayExpr="name"
                            valueExpr="id"
                            value={role}
                            onValueChanged={(e) => setRole(e.value ?? null)}
                            width="100%"
                            grouped={true}
                            placeholder="Select a role..."
                        />
                    </div>
                )}

                {needsStatus && (
                    <div>
                        <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Statuses</label>
                        <TagBox
                            items={statusItems}
                            displayExpr="name"
                            valueExpr="id"
                            value={statuses}
                            onValueChanged={(e) => setStatuses(e.value ?? [])}
                            width="100%"
                            placeholder="Select statuses..."
                            showSelectionControls={true}
                        />
                    </div>
                )}

                <div>
                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Permissions</label>
                    <TagBox
                        items={permissionItems}
                        displayExpr="name"
                        valueExpr="id"
                        value={perms}
                        onValueChanged={(e) => setPerms(e.value ?? [])}
                        width="100%"
                        placeholder="Select permissions..."
                        showSelectionControls={true}
                        itemRender={renderPermissionItem}
                    />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
                    <Button text="Cancel" stylingMode="outlined" onClick={onCancel} />
                    <Button
                        text="Add"
                        type="default"
                        stylingMode="contained"
                        disabled={!canAdd}
                        onClick={() => onAdd(role, statuses, perms)}
                    />
                </div>
            </div>
        </Popup>
    );
}
